import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type OneTimeShareMeta } from "../api/client";
import { decryptShare, type ShareField } from "../crypto/otshare";

// Standalone recipient page for a one-time secret share (/s/{id}#k=…&s=…). The recipient has no
// account; this view is mounted OUTSIDE the auth gate and loads no WASM. The decryption key lives in
// the URL fragment (never sent to the server) — the server only ever returns ciphertext, exactly once.

function parseFragment(): { k: string; s: string } | null {
  const h = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  const p = new URLSearchParams(h);
  const k = p.get("k");
  const s = p.get("s");
  return k && s ? { k, s } : null;
}

type Phase = "loading" | "meta" | "revealed" | "gone" | "error";

export function PublicShareView() {
  const id = decodeURIComponent(location.pathname.replace(/^\/s\//, "").replace(/\/$/, ""));
  const frag = parseFragment();
  const invalid = !id || !frag;

  const [phase, setPhase] = useState<Phase>(invalid ? "error" : "loading");
  const [meta, setMeta] = useState<OneTimeShareMeta | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [fields, setFields] = useState<ShareField[]>([]);
  const [note, setNote] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(invalid ? "This link is incomplete or malformed." : null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (invalid) return;
    let alive = true;
    api
      .getOneTimeShareMeta(id)
      .then((m) => {
        if (!alive) return;
        setMeta(m);
        if (m.status === "pending") setPhase("meta");
        else setPhase("gone");
      })
      .catch(() => {
        if (alive) {
          setError("Could not reach the server.");
          setPhase("error");
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const reveal = useCallback(async () => {
    if (!frag) return;
    setBusy(true);
    setError(null);
    try {
      // Single-use: this POST consumes the link server-side. From here the ciphertext exists only in
      // this tab — a wrong passphrase cannot be retried against the server.
      const r = await api.revealOneTimeShare(id);
      try {
        const payload = await decryptShare(r.ciphertext, frag.k, frag.s, passphrase || undefined);
        setFields(payload.fields || []);
        setNote(payload.note);
        setPhase("revealed");
      } catch {
        setError(
          meta?.passphrase_required
            ? "Wrong passphrase — and this link has now been used up. Ask the sender for a new one."
            : "This link could not be decrypted (the key in the link may be wrong).",
        );
        setPhase("error");
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setPhase("gone");
        setMeta((m) => (m ? { ...m, status: "consumed" } : m));
      } else {
        setError(e instanceof ApiError ? e.message : "Reveal failed.");
        setPhase("error");
      }
    } finally {
      setBusy(false);
    }
  }, [frag, id, passphrase, meta]);

  return (
    <div className="grid min-h-screen place-items-center bg-bg px-4 py-10 text-fg">
      <div className="w-full max-w-md rounded-lg border border-border bg-panel shadow-2xl">
        <div className="border-b border-border px-5 py-4">
          <div className="text-sm font-semibold">One-time secret</div>
          <div className="text-xs text-muted">Shown once, then destroyed.</div>
        </div>
        <div className="px-5 py-5 text-sm">
          {phase === "loading" && <p className="text-muted">Loading…</p>}

          {phase === "error" && <p className="text-danger">{error}</p>}

          {phase === "gone" && (
            <div className="flex flex-col gap-2">
              <p className="font-medium">This secret is no longer available.</p>
              <p className="text-xs text-muted">
                {meta?.status === "expired"
                  ? "The link expired before it was opened."
                  : "It has already been viewed once. For security, a one-time link can only be opened a single time."}
              </p>
            </div>
          )}

          {phase === "meta" && meta && (
            <div className="flex flex-col gap-4">
              {meta.label && (
                <div className="rounded border border-border bg-panel2 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted">Note from sender</div>
                  <div className="break-words">{meta.label}</div>
                </div>
              )}
              <div className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                You can open this secret <strong>only once</strong>. Make sure you can copy it now before
                revealing.
              </div>
              {meta.passphrase_required && (
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted">Passphrase (from the sender, out of band)</span>
                  <div className="flex gap-2">
                    <input
                      type={showPass ? "text" : "password"}
                      value={passphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      autoFocus
                      className="flex-1 rounded border border-border bg-panel2 px-2 py-1.5 font-mono"
                      placeholder="passphrase"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((v) => !v)}
                      className="rounded border border-border px-2 text-xs hover:border-accent"
                    >
                      {showPass ? "Hide" : "Show"}
                    </button>
                  </div>
                  <span className="text-[11px] text-danger">
                    You get one attempt — a wrong passphrase still consumes the link.
                  </span>
                </label>
              )}
              <button
                onClick={() => void reveal()}
                disabled={busy || (meta.passphrase_required && !passphrase)}
                className="rounded bg-accent px-3 py-2 font-medium text-white hover:bg-accent2 disabled:opacity-60"
              >
                {busy ? "Revealing…" : "Reveal secret"}
              </button>
            </div>
          )}

          {phase === "revealed" && (
            <div className="flex flex-col gap-3">
              <div className="rounded border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-muted">
                This is the only time these details will be shown. The link is now used up.
              </div>
              {fields.map((f, i) => (
                <RevealedField key={i} field={f} />
              ))}
              {note && (
                <div className="flex flex-col gap-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted">Note</div>
                  <pre className="whitespace-pre-wrap break-words rounded bg-panel2 px-2 py-1.5 font-mono text-xs">{note}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RevealedField({ field }: { field: ShareField }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard
      .writeText(field.value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => undefined);
  };
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[11px] uppercase tracking-wide text-muted">{field.label}</div>
      <div className="flex items-start gap-2">
        <span className="flex-1 break-all font-mono">{field.value}</span>
        <button onClick={copy} className="shrink-0 rounded border border-border px-2 py-0.5 text-xs hover:border-accent">
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
